import { useEffect, useState } from "react";
import api, { fmtBRL, fmtInt, fmtPct, fmtNum } from "../lib/api";
import PageHeader from "../components/PageHeader";
import KPICard from "../components/KPICard";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { ArrowUpRight, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [refreshingSales, setRefreshingSales] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/dashboard/summary").then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const refreshSales = async () => {
    setRefreshingSales(true);
    const t = toast.loading("Atualizando vendas do PDV…");
    try {
      const r = await api.post("/sync/quick");
      const ins = r.data?.inserted ?? 0;
      const upd = r.data?.updated ?? 0;
      toast.success(`Vendas atualizadas: ${ins} novo(s) cupom(ns), ${upd} atualizados`, { id: t });
      load();
    } catch (e) {
      toast.error("Falha ao atualizar vendas", { id: t });
    } finally {
      setRefreshingSales(false);
    }
  };

  const seedAndReload = async () => {
    setSeeding(true);
    try {
      await api.post("/seed/excel");
      toast.success("Dados importados das planilhas de exemplo");
      load();
    } catch (e) {
      toast.error("Falha ao importar planilhas");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return <div className="font-body" style={{ color: "var(--ifl-muted)" }} data-testid="dashboard-loading">Carregando indicadores…</div>;
  if (!data) return null;
  const { kpis, revenue_series, payment_breakdown } = data;

  const colors = {
    text: "#0F1B2D",
    muted: "#6B6E76",
    border: "#E5E1D8",
    accent: "#B8533B",
    accent2: "#C48A2E",
    success: "#4F7C53",
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Visão Geral"
        title="QUANDO O CUSTO"
        accent="é claro."
        description="Indicadores operacionais consolidando entradas Omie, vendas PDV e fichas técnicas."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={refreshSales}
              className="ifl-btn"
              disabled={refreshingSales}
              data-testid="dashboard-refresh-sales-btn"
            >
              <RefreshCw size={14} strokeWidth={2} className={refreshingSales ? "ifl-pulse" : ""} />
              {refreshingSales ? "Atualizando…" : "Atualizar vendas"}
            </button>
            <button onClick={seedAndReload} className="ifl-btn-outline" data-testid="seed-btn" disabled={seeding}>
              {seeding ? "Importando…" : "Importar planilhas de exemplo"}
            </button>
          </div>
        }
        testId="dashboard-header"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KPICard
          label="Receita do período"
          value={fmtBRL(kpis.total_revenue)}
          hint={`${fmtInt(kpis.total_tickets)} cupons · Ticket médio ${fmtBRL(kpis.avg_ticket)}`}
          accentColor={colors.success}
          testId="kpi-revenue"
        />
        <KPICard
          label="Valor em estoque"
          value={fmtBRL(kpis.inventory_value)}
          hint={`${fmtInt(kpis.ok_items)} itens · CMC consolidado`}
          accentColor={colors.text}
          testId="kpi-inventory-value"
        />
        <KPICard
          label="CMV médio das fichas"
          value={fmtPct(kpis.avg_recipe_cmv_pct)}
          hint={`${fmtInt(kpis.recipes_count)} fichas cadastradas`}
          accentColor={colors.accent2}
          testId="kpi-cmv"
        />
        <KPICard
          label="Itens críticos"
          value={fmtInt(kpis.negative_items + kpis.zero_items)}
          hint={`${fmtInt(kpis.negative_items)} negativos · ${fmtInt(kpis.zero_items)} zerados`}
          accentColor={colors.accent}
          testId="kpi-critical"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="ifl-card p-6 lg:col-span-2 ifl-fade-up" data-testid="revenue-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Receita por dia</div>
              <div className="font-italic-serif text-xl mt-1">faturamento PDV.</div>
            </div>
            <ArrowUpRight size={16} strokeWidth={1.5} style={{ color: colors.muted }} />
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={revenue_series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke={colors.border} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke={colors.muted} fontSize={11} tick={{ fontFamily: "Space Grotesk" }} />
                <YAxis stroke={colors.muted} fontSize={11} tick={{ fontFamily: "Space Grotesk" }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 12, fontFamily: "DM Sans" }}
                  formatter={(v) => fmtBRL(v)}
                />
                <Line type="monotone" dataKey="revenue" stroke={colors.text} strokeWidth={1.5} dot={{ r: 2, fill: colors.text }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="ifl-card p-6 ifl-fade-up" data-testid="payments-chart-card">
          <div className="mb-4">
            <div className="label-caps">Formas de pagamento</div>
            <div className="font-italic-serif text-xl mt-1">como entram.</div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={payment_breakdown} layout="vertical" margin={{ top: 6, right: 10, left: 4, bottom: 0 }}>
                <CartesianGrid stroke={colors.border} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" stroke={colors.muted} fontSize={11} tick={{ fontFamily: "Space Grotesk" }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" width={90} stroke={colors.muted} fontSize={10} tick={{ fontFamily: "Space Grotesk" }} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 12, fontFamily: "DM Sans" }}
                  formatter={(v) => fmtBRL(v)}
                />
                <Bar dataKey="value" fill={colors.accent} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {kpis.negative_items > 0 && (
        <div className="ifl-card p-6 mt-6 flex items-start gap-4 ifl-fade-up" style={{ borderColor: "var(--ifl-terracotta)", borderWidth: 1 }} data-testid="negative-alert">
          <AlertTriangle size={20} strokeWidth={1.5} style={{ color: "var(--ifl-terracotta)" }} />
          <div>
            <div className="heading-caps text-sm mb-1">Estoque negativo detectado</div>
            <div className="font-body text-sm" style={{ color: "var(--ifl-muted)" }}>
              {fmtInt(kpis.negative_items)} itens com saldo negativo — provável diferença entre entradas (NF) e baixas (venda). Verifique a aba <strong style={{ color: "var(--ifl-text)" }}>Conciliação</strong>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
